import { EventMessage, EventType, InteractionType } from '@azure/msal-browser';
import { Router } from '@angular/router';

/**
 * SessionStorage key holding the URL the user originally requested before an
 * interactive redirect login.
 *
 * MSAL's default `navigateToLoginRequestUrl: true` restores that URL with a
 * hard `location.href` navigation after the auth response was processed on the
 * `redirectUri` page — a third full bootstrap of the app in a single login
 * (measured: ~2s extra on mid-range hardware). We disable that default per
 * call in `BdAuthenticationRoot` (`handleRedirectObservable({
 * navigateToLoginRequestUrl: false })` — in msal-browser v5 the flag lives in
 * HandleRedirectPromiseOptions, not the auth config) and restore the target
 * client-side through the Angular router instead: `storeRedirectTarget`
 * records it right before MSAL navigates away to B2C,
 * `restoreRedirectTarget` replays it after the auth result arrived.
 */
export const BD_REDIRECT_TARGET_KEY = 'bd.auth.redirect-target';

function isRedirectInteractionStart(message: EventMessage): boolean {
  // msal-browser v5 funnels loginRedirect through acquireTokenRedirect, which
  // emits ACQUIRE_TOKEN_START with InteractionType.Redirect (LOGIN_START no
  // longer exists).
  return (
    message.eventType === EventType.ACQUIRE_TOKEN_START &&
    message.interactionType === InteractionType.Redirect
  );
}

/**
 * Records the redirect target before MSAL navigates away to the authority.
 * Wired as an MSAL event callback — `loginRedirect`/`acquireTokenRedirect`
 * emit `ACQUIRE_TOKEN_START` with the request (incl. the `redirectStartPage`
 * the MsalGuard computed from the attempted route) as payload.
 */
export function storeRedirectTarget(message: EventMessage): void {
  if (typeof window === 'undefined' || !isRedirectInteractionStart(message)) {
    return;
  }
  const payload = message.payload as { redirectStartPage?: string } | null;
  const startPage = payload?.redirectStartPage || window.location.href;
  try {
    window.sessionStorage.setItem(BD_REDIRECT_TARGET_KEY, startPage);
  } catch {
    // sessionStorage unavailable — losing deep-link restore is acceptable
  }
}

/**
 * Navigates back to the stored redirect target after MSAL processed the auth
 * response on the `redirectUri` page. Same-origin targets only; no-op when
 * nothing was stored or the app is already on the target URL.
 */
export function restoreRedirectTarget(router: Router): void {
  if (typeof window === 'undefined') {
    return;
  }
  // No initializer: the catch returns, so the only readable value is the one
  // the try assigns (ESLint 10's no-useless-assignment).
  let target: string | null;
  try {
    target = window.sessionStorage.getItem(BD_REDIRECT_TARGET_KEY);
    window.sessionStorage.removeItem(BD_REDIRECT_TARGET_KEY);
  } catch {
    return;
  }
  if (!target) {
    return;
  }
  let url: URL;
  try {
    url = new URL(target, window.location.origin);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) {
    return;
  }
  const path = url.pathname + url.search + url.hash;
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  if (path === current) {
    return;
  }
  void router.navigateByUrl(path);
}
